'use client';

import React, { Fragment, useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Linkedin, Facebook, Instagram, Send, Loader2, Calendar, ChevronDown, MessageCircle } from 'lucide-react';
import { whatsappHref, DEFAULT_WHATSAPP_MESSAGE, GBP_URL, PHONE_DISPLAY, PHONE_TEL } from '@/lib/contact';

// ─── Contact Hero ───────────────────────────────────────────────────────────

function ContactHero() {
    return (
        <section className="relative pt-40 pb-20 px-6 md:px-24 bg-white overflow-hidden">
            <div className="max-w-7xl mx-auto relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="text-center"
                >
                    <span className="inline-block px-4 py-1.5 rounded-full bg-[#ebebf8] text-[#3333AA] text-xs font-bold tracking-widest uppercase mb-6">
                        Get In Touch
                    </span>
                    <h1 className="text-6xl md:text-8xl font-black text-gray-900 leading-[0.95] mb-8 tracking-wide"
                        style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}>
                        Let's Engineer Your <br />
                        <span className="text-[#3333AA]">Next Growth Phase.</span>
                    </h1>
                    <p className="text-xl md:text-2xl text-gray-500 max-w-3xl mx-auto leading-relaxed font-light">
                        Whether you're looking to scale a proven model or build a new growth engine from scratch, our team is ready to deliver systematic performance.
                    </p>
                </motion.div>
            </div>
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#ebebf8] rounded-full blur-[120px] -z-0 opacity-50 translate-x-1/3 -translate-y-1/3" />
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#ebebf8] rounded-full blur-[120px] -z-0 opacity-50 -translate-x-1/3 translate-y-1/3" />
        </section>
    );
}

// ─── Contact Form Section ────────────────────────────────────────────────────

function ContactFormSection() {
    const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');
        try {
            const res = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...formData, source: 'Contact Page' }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Something went wrong.');
            setIsSubmitted(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Something went wrong.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="relative py-24 px-6 md:px-24 overflow-hidden" style={{ background: '#111111' }}>
            <div className="max-w-7xl mx-auto relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 md:gap-24 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <h2 className="text-4xl md:text-5xl font-black mb-8"
                            style={{ color: '#FBFDFD', letterSpacing: '0.04em' }}>
                            Contact <span style={{ color: '#CEFF1A' }}>Information</span>
                        </h2>
                        <p className="text-lg mb-12 leading-relaxed"
                            style={{ color: '#71717a' }}>
                            Have questions about our systematic approach? Reach out directly or fill out the form. We respond to all inquiries within 24 hours.
                        </p>

                        <div className="space-y-8 mb-16">
                            {[
                                { icon: <Mail className="w-6 h-6" />, label: "Email Us", val: "info@leadworkz.com", href: "mailto:info@leadworkz.com" },
                                { icon: <Phone className="w-6 h-6" />, label: "Call Us", val: PHONE_DISPLAY, href: PHONE_TEL, extra: [{ val: "+91 9632893377", href: "tel:+919632893377" }] },
                                { icon: <MessageCircle className="w-6 h-6" />, label: "WhatsApp Us", val: "Chat on WhatsApp", href: whatsappHref(DEFAULT_WHATSAPP_MESSAGE) },
                                { icon: <MapPin className="w-6 h-6" />, label: "Our Location", val: "Bengaluru, Karnataka 560062", href: GBP_URL }
                            ].map((item, i) => item.extra ? (
                                <div key={i} className="flex items-start gap-6 group w-fit">
                                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-all duration-300 shrink-0" style={{ background: 'rgba(255,255,255,0.08)', color: '#CEFF1A' }}>
                                        {item.icon}
                                    </div>
                                    <div>
                                        <div className="text-sm font-bold uppercase tracking-widest mb-1" style={{ color: '#71717a' }}>{item.label}</div>
                                        <div className="flex flex-col gap-1 xl:flex-row xl:items-center xl:gap-4">
                                            {[{ val: item.val, href: item.href }, ...item.extra].map((num, j) => (
                                                <Fragment key={num.href}>
                                                    {j > 0 && <span aria-hidden="true" className="hidden h-5 w-px xl:block" style={{ background: 'rgba(255,255,255,0.2)' }} />}
                                                    <a href={num.href} className="whitespace-nowrap text-xl font-bold transition-colors duration-300 hover:text-[#CEFF1A]" style={{ color: '#FBFDFD' }}>{num.val}</a>
                                                </Fragment>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <a
                                    key={i}
                                    href={item.href}
                                    target={item.href.startsWith('http') ? "_blank" : undefined}
                                    rel={item.href.startsWith('http') ? "noopener noreferrer" : undefined}
                                    className="flex items-start gap-6 group cursor-pointer w-fit"
                                >
                                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-all duration-300 shrink-0" style={{ background: 'rgba(255,255,255,0.08)', color: '#CEFF1A' }}>
                                        {item.icon}
                                    </div>
                                    <div>
                                        <div className="text-sm font-bold uppercase tracking-widest mb-1" style={{ color: '#71717a' }}>{item.label}</div>
                                        <div className="text-xl font-bold transition-colors duration-300 group-hover:text-[#CEFF1A]" style={{ color: '#FBFDFD' }}>{item.val}</div>
                                    </div>
                                </a>
                            ))}
                        </div>

                        <div>
                            <div className="text-sm font-bold uppercase tracking-widest mb-6" style={{ color: '#71717a' }}>Follow Our Journey</div>
                            <div className="flex gap-4">
                                {[
                                    { icon: <Linkedin />, href: "https://www.linkedin.com/company/https-www.leadworkz.com-/", hoverBg: '#0A66C2' },
                                    { icon: <Instagram />, href: "https://www.instagram.com/lead_workz?igsh=MWpsanN3OTBjbmgzcA==", hoverBg: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)' },
                                    { icon: <Facebook />, href: "https://www.facebook.com/share/1CRWRhnrVm/?mibextid=wwXIfr", hoverBg: '#1877F2' }
                                ].map((social, i) => (
                                    <a
                                        key={i}
                                        href={social.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-12 h-12 rounded-xl flex items-center justify-center hover:-translate-y-1 transition-all duration-300"
                                        style={{ background: 'rgba(255,255,255,0.08)', color: '#FBFDFD' }}
                                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = social.hoverBg; }}
                                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.08)'; }}
                                    >
                                        {social.icon}
                                    </a>
                                ))}
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <div className="bg-white p-6 md:p-8 rounded-[2rem] shadow-2xl border border-gray-100 relative group">
                            <div className="absolute -inset-0.5 bg-gradient-to-br from-[#3333AA] to-[#3333AA] rounded-[2rem] opacity-0 group-hover:opacity-10 transition-opacity duration-500 blur-xl -z-10" />

                            {isSubmitted ? (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="text-center py-12"
                                >
                                    <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Send className="w-8 h-8" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}>Message Sent!</h3>
                                    <p className="text-gray-500 text-sm">We'll get back to you within 24 hours.</p>
                                    <button
                                        onClick={() => { setIsSubmitted(false); setFormData({ name: '', email: '', phone: '', subject: '', message: '' }); }}
                                        className="mt-6 text-[#3333AA] font-bold hover:underline text-sm"
                                    >
                                        Send another message
                                    </button>
                                </motion.div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-700 ml-1">Full Name</label>
                                            <input type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="John Doe"
                                                className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-100 focus:border-[#3333AA] focus:bg-white focus:outline-none transition-all text-gray-900 text-sm" />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-700 ml-1">Email Address</label>
                                            <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="john@example.com"
                                                className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-100 focus:border-[#3333AA] focus:bg-white focus:outline-none transition-all text-gray-900 text-sm" />
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-gray-700 ml-1">Phone Number</label>
                                        <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+91 XXXXX XXXXX"
                                            className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-100 focus:border-[#3333AA] focus:bg-white focus:outline-none transition-all text-gray-900 text-sm" />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-gray-700 ml-1">Subject / Service</label>
                                        <div className="relative">
                                            <select
                                                name="subject"
                                                value={formData.subject}
                                                onChange={handleChange}
                                                required
                                                className={`w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-100 focus:border-[#3333AA] focus:bg-white focus:outline-none transition-all appearance-none cursor-pointer pr-10 text-sm ${formData.subject ? 'text-gray-900 font-medium' : 'text-gray-400'}`}
                                            >
                                                <option value="" disabled hidden>Select a service...</option>
                                                <option value="Performance Marketing">Performance Marketing</option>
                                                <option value="Visual Communication & Branding">Visual Communication & Branding</option>
                                                <option value="Digital Product Design & Development">Digital Product Design & Development</option>
                                                <option value="Search & Visibility (SEO & SEM)">Search & Visibility (SEO & SEM)</option>
                                                <option value="Social Media Marketing">Social Media Marketing</option>
                                                <option value="General Inquiry / Other">General Inquiry / Other</option>
                                            </select>
                                            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                                                <ChevronDown className="w-4 h-4" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-gray-700 ml-1">Message</label>
                                        <textarea rows={4} name="message" value={formData.message} onChange={handleChange} required placeholder="Tell us about your project..."
                                            className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-100 focus:border-[#3333AA] focus:bg-white focus:outline-none transition-all resize-none text-gray-900 text-sm" />
                                    </div>
                                    {error && <p className="text-red-500 text-xs text-center font-medium">{error}</p>}
                                    <button type="submit" disabled={isSubmitting}
                                        className="w-full bg-[#3333AA] hover:bg-[#2828A0] disabled:opacity-70 text-white font-black py-4 rounded-xl flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-95 shadow-xl shadow-[#bebee8]">
                                        {isSubmitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <>Send Message <Send className="w-4 h-4" /></>}
                                    </button>
                                </form>
                            )}
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

// ─── Map Section ─────────────────────────────────────────────────────────────

function MapSection() {
    const fallbackSrc = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3889.390232467341!2d77.5514148!3d12.8852023!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae3fb62e6608e3:0x569d070680e86ae!2sLead%20Workz!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin";

    return (
        <section className="relative py-24 px-6 md:px-24 bg-white overflow-hidden">
            <div className="max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="relative rounded-[3rem] overflow-hidden shadow-2xl border-8 border-gray-50 h-[500px] md:h-[600px] group"
                >
                    <iframe src={fallbackSrc} width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="transition-all duration-700" />
                    <div className="absolute bottom-8 left-8 right-8 md:bottom-12 md:left-12 md:right-auto bg-white p-8 rounded-3xl shadow-2xl max-w-md border border-gray-100 transform group-hover:-translate-y-2 transition-transform duration-500">
                        <div className="text-[#3333AA] font-bold uppercase tracking-widest text-xs mb-3">Headquarters</div>
                        <h3 className="text-xl font-black text-gray-900 mb-2 font-clash">Lead Workz</h3>
                        <p className="text-gray-500 leading-relaxed font-medium font-manrope">
                            Metro Station, 60, Kanakapura Main Rd, Doddakallasandra, Bengaluru, Karnataka 560062
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}

// ─── Contact Form Modal ───────────────────────────────────────────────────────

function ContactFormModal({ onClose }: { onClose: () => void }) {
    const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        await new Promise(r => setTimeout(r, 1500));
        setIsSubmitting(false);
        setIsSubmitted(true);
    };

    return (
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4" onClick={onClose}>
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            <motion.div
                initial={{ opacity: 0, scale: 0.92, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.92, y: 20 }}
                transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
                className="relative w-full max-w-lg bg-white rounded-[2rem] p-8 shadow-2xl z-10"
                onClick={e => e.stopPropagation()}
            >
                {isSubmitted ? (
                    <div className="text-center py-8">
                        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all text-lg font-bold">✕</button>
                        <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Send className="w-8 h-8" />
                        </div>
                        <h3 className="text-xl font-black mb-2" style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}>Message Sent!</h3>
                        <p className="text-gray-500 text-sm mb-6">We'll get back to you within 24 hours.</p>
                        <button onClick={onClose} className="px-6 py-2 rounded-full font-bold text-sm" style={{ background: '#080400', color: '#CEFF1A' }}>Close</button>
                    </div>
                ) : (
                    <>
                        {/* Header row */}
                        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all text-lg font-bold">✕</button>
                        <div className="text-center mb-1">
                            <h3 className="text-2xl font-black" style={{ letterSpacing: '0.02em' }}>
                                Schedule Your<br /><span style={{ color: '#3730A3' }}>Strategy Audit</span>
                            </h3>
                        </div>
                        <p className="text-sm text-gray-500 mb-6 text-center">Fill in your details and we'll be in touch shortly.</p>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-bold text-gray-600 mb-1 block">Full Name</label>
                                    <input type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="John Doe"
                                        className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-100 focus:border-[#3730A3] focus:outline-none text-sm transition-all text-gray-900 placeholder:text-gray-400" />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-gray-600 mb-1 block">Email</label>
                                    <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="john@example.com"
                                        className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-100 focus:border-[#3730A3] focus:outline-none text-sm transition-all text-gray-900 placeholder:text-gray-400" />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-gray-600 mb-1 block">Phone Number</label>
                                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+91 XXXXX XXXXX"
                                    className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-100 focus:border-[#3730A3] focus:outline-none text-sm transition-all text-gray-900 placeholder:text-gray-400" />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-gray-600 mb-1 block">Subject / Service</label>
                                <div className="relative">
                                    <select name="subject" value={formData.subject} onChange={handleChange} required
                                        className={`w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-100 focus:border-[#3730A3] focus:outline-none text-sm transition-all appearance-none pr-10 ${formData.subject ? 'text-gray-900' : 'text-gray-400'}`}>
                                        <option value="" disabled hidden>Select a service...</option>
                                        <option value="Performance Marketing">Performance Marketing</option>
                                        <option value="Visual Communication & Branding">Visual Communication & Branding</option>
                                        <option value="Digital Product Design & Development">Digital Product Design & Development</option>
                                        <option value="Search & Visibility (SEO & SEM)">Search & Visibility (SEO & SEM)</option>
                                        <option value="Social Media Marketing">Social Media Marketing</option>
                                        <option value="General Inquiry / Other">General Inquiry / Other</option>
                                    </select>
                                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-gray-600 mb-1 block">Message</label>
                                <textarea rows={3} name="message" value={formData.message} onChange={handleChange} required placeholder="Tell us about your project..."
                                    className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-100 focus:border-[#3730A3] focus:outline-none text-sm transition-all resize-none text-gray-900 placeholder:text-gray-400" />
                            </div>
                            <button type="submit" disabled={isSubmitting}
                                className="w-full py-4 rounded-xl font-black text-sm flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-95"
                                style={{ background: '#080400', color: '#CEFF1A' }}>
                                {isSubmitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <>Send Message <Send className="w-4 h-4" /></>}
                            </button>
                        </form>
                    </>
                )}
            </motion.div>
        </div>
    );
}

// ─── Contact CTA ─────────────────────────────────────────────────────────────

function ContactCTA() {
    const [showModal, setShowModal] = useState(false);

    return (
        <>
        {showModal && <ContactFormModal onClose={() => setShowModal(false)} />}
        <section className="relative py-32 px-6 md:px-24 overflow-hidden">
            <div className="max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="relative rounded-[3rem] p-12 md:p-24 overflow-hidden"
                    style={{ background: '#080400', boxShadow: '0 25px 60px rgba(0,0,0,0.3)' }}
                >
                    <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" style={{ background: 'rgba(55,48,163,0.12)' }} />
                    <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full blur-3xl -translate-x-1/2 translate-y-1/2" style={{ background: 'rgba(0,71,171,0.1)' }} />

                    <div className="relative z-10 max-w-3xl mx-auto text-center">
                        <span className="inline-block px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase mb-8 border"
                            style={{ background: 'rgba(206,255,26,0.08)', borderColor: 'rgba(206,255,26,0.2)', color: '#CEFF1A' }}>
                            Get In Touch
                        </span>
                        <h2 className="text-3xl md:text-5xl font-black leading-tight mb-8"
                            style={{ color: '#FBFDFD', letterSpacing: '0.03em' }}>
                            <span style={{ display: 'block' }}>Ready to transform your</span>
                            <span style={{ color: '#CEFF1A', display: 'block' }}>Growth Architecture?</span>
                        </h2>
                        <p className="text-xl leading-relaxed mb-10 mx-auto"
                            style={{ color: 'rgba(251,253,253,0.65)' }}>
                            Stop guessing. Start engineering. Book a 30-minute strategy audit where we'll identify the three biggest bottlenecks in your current system.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button onClick={() => setShowModal(true)} className="px-10 py-5 rounded-full font-black flex items-center justify-center gap-3 transition-all hover:scale-105 shadow-xl"
                                style={{ background: '#CEFF1A', color: '#080400' }}>
                                <Calendar className="w-5 h-5" /> Schedule Your Strategy Audit
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
        </>
    );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function ContactPage() {
    return (
        <main className="min-h-screen bg-white">
            <ContactHero />
            <ContactFormSection />
            <MapSection />
            <ContactCTA />
        </main>
    );
}
